<template>
    <div class="notification-modal">
        <header>
            <div class="left">
                <div class="title">{{ $t('notification.notifications') }}</div>
                <span class="badge" v-if="unreadNotificationCount > 0">{{ unreadNotificationCount }}</span>
            </div>
            <div class="right">
                <SupsisButton small ghost @click="toggleInclude">
                    {{ filter.includeAll ? $t('notification.showOnlyUnread') : $t('notification.showAll') }}
                </SupsisButton>
                <SupsisButton small ghost @click="markAsSeenAll">
                    <i class="material-icons">done</i>
                    {{ $t('mail.markAsAllRead') }}
                </SupsisButton>
            </div>
        </header>
        <section class="content">
            <NotificationListView
                :notifications="data.items"
                :toggleReadState="toggleReadState"
                :groupByDate="false"
                :minimized="true"
                :onCardClick="onCardClick"
                @load-more="loadMore"
            />
        </section>
        <footer>
            <SupsisButton small ghost @click="viewAll">
                <i class="material-icons">query_stats</i>
                {{ $t('notification.showAll') }}
            </SupsisButton>
            <SupsisButton small ghost @click="gotoSettings">
                <i class="material-icons">settings</i>
                {{ $t('notification.adjustSettings') }}
            </SupsisButton>
        </footer>
    </div>
</template>
<script>
import Vue from "vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import NotificationListView from "./NotificationListView.vue";
import { ACTIVITY_API } from "../../../api/activity";
import _ from "lodash";
import { mapState, mapActions } from "vuex";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { filter } from "rxjs/operators";

export default Vue.extend({
    name: "NotificationPanel",
    components: { NotificationListView, SupsisButton },
    data() {
        return {
            filter: {
                includeAll: false,
                cursor: {
                    page: 1,
                    perPage: 20,
                },
            },
            loading: true,
            notifications: [],

            data: {
                total: 0,
                items: [],
            },
            subscribes: [],
            cursor: {
                page: 1,
                perPage: 20,
            },
        };
    },
    computed: {
        ...mapState({
            popovers: (state) => state.app.headerPopoverStatus,
            unreadNotificationCount: (state) => state.app.unreadNotificationCount,
        }),
    },
    methods: {
        ...mapActions({
            setNotificationCount: "app/setNotificationCount",
        }),
        toggleReadState() {},
        onCardClick() {},
        gotoSettings() {
            this.popovers.notifications = false;
            this.$router.push("/settings/notifications");
        },
        viewAll() {
            this.popovers.notifications = false;
            this.$router.push("/notifications");
        },
        fetchNotifications(pagination) {
            const fn = this.filter.includeAll
                ? ACTIVITY_API.NOTIFICATION.getAllNotifications
                : ACTIVITY_API.NOTIFICATION.getUnreadNotifications;
            this.loading = true;
            fn(this.cursor)
                .then((res) => {
                    const { count, items } = res.data;
                    this.data.items = _.uniqBy(pagination ? [...this.data.items, ...items] : items, (q) => q._id);
                    this.data.total = count;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadMore() {
            if (Math.ceil(this.data.total / this.cursor.perPage) > this.cursor.page) {
                this.cursor.page++;
            }
        },
        toggleInclude() {
            this.filter.includeAll = !this.filter.includeAll;
        },
        markAsSeenAll() {
            ACTIVITY_API.NOTIFICATION.markAsSeenAll().then(() => {
                this.data.items = this.data.items.map((item) => {
                    item.seen = true;
                    return item;
                });
                this.setNotificationCount(0);
            });
        },
    },
    mounted() {
        this.fetchNotifications();
        const notificationUpdated = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.NOTIFICATION_UPDATED))
            .subscribe((x) => {
                const { id, seen } = x.payload;
                this.data.items = this.data.items.map((item) => {
                    if (item._id === id) item.seen = seen;
                    return item;
                });
            });

        this.subscribes.push(() => {
            notificationUpdated.unsubscribe();
        });
    },
    beforeDestroy() {
        this.subscribes.forEach((fn) => fn());
    },
    watch: {
        filter: {
            handler() {
                this.fetchNotifications();
            },
            deep: true,
        },
        cursor: {
            handler() {
                this.fetchNotifications(true);
            },
            deep: true,
        },
    },
});
</script>

<style lang="scss">
.notification-modal {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    font-size: 14px;
    font-weight: 400;
    color: var(--color-text-default);
    line-height: 2.4rem;
    position: relative;
    width: 550px;
    background: var(--color-bg-default);
    box-shadow: -13px 22px 41px 0px rgba(0, 0, 0, 0.3);

    .supsis-button {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start;
        gap: 5px;
    }

    header {
        background: var(--color-bg-default);
        display: flex;
        flex-flow: row;
        align-items: center;
        justify-content: space-between;
        position: relative;
        padding: 8px 12px;
        border-bottom: 1px solid var(--color-border-default);
        border-radius: 4px 4px 0 0;

        .left {
            display: flex;
            gap: 5px;
            align-items: center;
            cursor: default;

            .title {
                font-size: 14px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .badge {
                background: var(--color-bg-critical-default);
                color: var(--color-text-oncritical-default);
                border-radius: 4px;
                margin-left: 8px;
                min-width: fit-content;
                display: inline-block;
                font-size: 11px;
                font-weight: 700;
                line-height: 16px;
                padding: 0 4px;
                letter-spacing: -0.24px;
                min-height: 16px;
                text-transform: uppercase;
                width: fit-content;
                align-items: center;
                text-decoration: none;
                height: fit-content;
            }
        }

        .right {
            display: flex;
            gap: 5px;

            .supsis-button {
                border: none;
                font-size: 14px;
                font-weight: 600;
                color: var(--color-text-default);
                background-color: transparent;
                letter-spacing: -0.24px;
                line-height: 24px;

                &:hover {
                    background-color: var(--color-bg-neutral-hover) !important;
                    border-color: transparent !important;
                    color: var(--color-text-hover) !important;
                }
            }
        }
    }

    footer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        border-top: 1px solid var(--color-border-default);
        border-radius: 0 0 4px 4px;

        .supsis-button:last-child {
            border-bottom: none;
        }

        .supsis-button {
            width: 100%;
            border: unset !important;
            font-size: 14px;
            font-weight: 600;
            color: var(--color-text-default);
            background-color: transparent;
            letter-spacing: -0.24px;
            line-height: 24px;
            border-bottom: 1px solid var(--color-border-default) !important;
            border-radius: unset !important;
            max-width: none !important;

            &:hover {
                background-color: var(--color-bg-neutral-hover) !important;
                border-color: transparent !important;
                color: var(--color-text-hover) !important;
            }
        }
    }

    .content {
        .loading {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 20px;
        }

        .empty {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 20px;
            gap: 10px;

            .material-icons {
                width: 50px;
                height: 50px;
                color: var(--color-text-weak-default);
            }
        }
    }
}
</style>
